// feature/notion-popover-dropdowns — popover filters + inline cell editing.
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { cases as casesApi, users as usersApi } from "../api/endpoints";
import Empty from "../components/Empty";
import { TipoPill, EstadoPill, SlaPill, TipoFalloPill, ContestadaTag, ESTADO_LABEL, TIPO_LABEL } from "../components/Pills";
import TipoFalloTabs, { filterByTipoFallo } from "../components/TipoFalloTabs";
import EstadoFilterPills, { filterByActiveStatus } from "../components/EstadoFilterPills";
import { formatTipo } from "../utils/case_taxonomy";
import CaseDetail from "./case-detail";
import { useDebounced } from "../components/useDebounced";
import { PopoverMultiSelect, PopoverCombobox, PersonaCombobox } from "../components/Popover";
import { useContextMenu } from "../components/ContextMenu";
import Confirm from "../components/Confirm";
import { useAuth } from "../auth/AuthContext";
import { rolesOf } from "../utils/permissions";
import { useToast } from "../components/Toast";
import { useHoverIntent } from "../hooks/useHoverIntent";
import { HoverFocusProvider, useHoverFocus } from "../context/HoverFocusContext";

const TIPOS = ["tutela", "fallo", "requerimiento", "desacato", "sancion"];
const ESTADOS = Object.keys(ESTADO_LABEL);

export default function CasesList() {
  return (
    <HoverFocusProvider>
      <CasesListInner />
    </HoverFocusProvider>
  );
}

// feature/reparto-time-filters — small pill button used by the Reparto cases
// list to toggle between Todos / Hoy / Esta semana.
function TimeFilterButton({ label, value, current, setter, count }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => setter(value)}
      className={active ? "" : "secondary"}
      style={{
        padding: "4px 10px",
        fontSize: 12,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span>{label}</span>
      {count != null && count > 0 && (
        <span style={{
          background: active ? "rgba(255,255,255,0.25)" : "var(--accent-mint-bg)",
          color: active ? "var(--on-accent)" : "var(--accent-mint)",
          fontSize: 10,
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          padding: "1px 6px",
          borderRadius: 10,
          minWidth: 16,
          textAlign: "center",
        }}>{count}</span>
      )}
    </button>
  );
}

function CasesListInner() {
  const { user } = useAuth();
  const toast = useToast();
  // feature/single-role-audit — multi-role aware.
  const canEdit = ["admin", "reparto"].some(r => rolesOf(user).includes(r));
  const canEditAbogado = canEdit || rolesOf(user).includes("directora");

  const [tipos, setTipos] = useState([]);
  const [estados, setEstados] = useState([]);
  const [abogadoId, setAbogadoId] = useState(null);
  const [abogadoLabel, setAbogadoLabel] = useState(null);
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 300);

  const [page, setPage] = useState(1);
  const PAGE = 50;

  const [allUsers, setAllUsers] = useState([]);
  const [data, setData] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryOpen = searchParams.get("open");
  const [openId, setOpenId] = useState(queryOpen ? Number(queryOpen) : null);
  const [defaultTab] = useState("general");
  useEffect(() => { if (queryOpen) setOpenId(Number(queryOpen)); }, [queryOpen]);
  function closePanel() { setOpenId(null); if (queryOpen) { searchParams.delete("open"); setSearchParams(searchParams, { replace: true }); } }

  useEffect(() => { usersApi.list().then(r => setAllUsers(r.data)); }, []);

  function load() {
    const params = {};
    if (tipos.length === 1)   params.tipo = tipos[0];
    if (estados.length === 1) params.estado = estados[0];
    if (abogadoId)            params.abogado = abogadoId;
    if (dq)                   params.q = dq;
    setData(null);
    casesApi.list(params)
      .then(r => {
        let rows = r.data;
        if (tipos.length > 1)   rows = rows.filter(c => tipos.includes(c.tipo));
        if (estados.length > 1) rows = rows.filter(c => estados.includes(c.estado));
        setData(rows);
      })
      .catch(() => setData([]));
  }
  useEffect(() => { load(); setPage(1); /* eslint-disable-next-line */ }, [tipos, estados, abogadoId, dq]);

  // Live SLA refresh
  useEffect(() => {
    const t = setInterval(() => setData(d => d && [...d]), 60_000);
    return () => clearInterval(t);
  }, []);

  // feature/reparto-time-filters. "Hoy / Esta semana / Todos" with
  // localStorage persistence. Bogotá Monday-start week (Sunday rolls back 6 days).
  const [timeFilter, setTimeFilter] = useState(() =>
    localStorage.getItem("eds.repartoTimeFilter") || "all"
  );
  useEffect(() => { localStorage.setItem("eds.repartoTimeFilter", timeFilter); }, [timeFilter]);

  const { todayStart, weekStart } = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const week = new Date(today);
    const dow = week.getDay(); // 0 = Sunday, 1 = Monday, ...
    const daysToMonday = dow === 0 ? -6 : 1 - dow;
    week.setDate(week.getDate() + daysToMonday);
    return { todayStart: today, weekStart: week };
  }, []);

  const todayCount = useMemo(
    () => (data || []).filter((c) => new Date(c.created_at) >= todayStart).length,
    [data, todayStart]
  );
  const weekCount = useMemo(
    () => (data || []).filter((c) => new Date(c.created_at) >= weekStart).length,
    [data, weekStart]
  );

  const filteredData = useMemo(() => {
    if (!data) return data;
    if (timeFilter === "today") return data.filter((c) => new Date(c.created_at) >= todayStart);
    if (timeFilter === "week")  return data.filter((c) => new Date(c.created_at) >= weekStart);
    return data;
  }, [data, timeFilter, todayStart, weekStart]);

  // Reset to page 1 whenever the time filter changes — the underlying row count
  // can shrink and a stale page would render empty.
  useEffect(() => { setPage(1); }, [timeFilter]);

  // feature/estado-filter-pills. Default to "activos" so
  // closed/contestada cases tuck behind a click. Sits between the time
  // filter and the tipo-fallo tabs in the filter chain so all four
  // composes cleanly.
  const [estadoFilter, setEstadoFilter] = useState("activos");
  useEffect(() => { setPage(1); }, [estadoFilter]);
  const estadoFiltered = useMemo(
    () => filterByActiveStatus(filteredData, estadoFilter),
    [filteredData, estadoFilter]
  );

  // feature/tipo-fallo-tabs. The Favorables/Desfavorables/Nulidad/
  // Sin clasificar tab bar shows up only when Angie has narrowed Tipo de
  // actuación to "fallo" specifically (single-select). Tabs are
  // meaningless when she's looking at mixed types — keep them hidden
  // there so the filter UI doesn't clutter.
  const [tipoFalloTab, setTipoFalloTab] = useState("todos");
  const showFalloTabs = tipos.length === 1 && tipos[0] === "fallo";
  // Reset the tab whenever Angie leaves the fallos slice; otherwise the
  // tab state stays stale and would re-apply the next time she narrows
  // back to fallo.
  useEffect(() => { if (!showFalloTabs && tipoFalloTab !== "todos") setTipoFalloTab("todos"); /* eslint-disable-next-line */ }, [showFalloTabs]);
  const tipoFalloFiltered = useMemo(() => {
    if (!estadoFiltered) return estadoFiltered;
    if (!showFalloTabs) return estadoFiltered;
    return filterByTipoFallo(estadoFiltered, tipoFalloTab);
  }, [estadoFiltered, showFalloTabs, tipoFalloTab]);
  // Reset page when the tab filter changes for the same reason as timeFilter.
  useEffect(() => { setPage(1); }, [tipoFalloTab]);

  // feature/nulidad-bandera — sprint86. Optional toggle so Angie can track
  // post-nulidad re-intakes as a group. Pure client-side filter on the
  // flag already returned by GET /api/cases (c.*); composes at the end of
  // the existing filter chain.
  const [nulidadOnly, setNulidadOnly] = useState(false);
  useEffect(() => { setPage(1); }, [nulidadOnly]);
  const nulidadCount = useMemo(
    () => (data || []).filter((c) => c.flag_vuelve_despues_de_nulidad).length,
    [data]
  );
  const nulidadFiltered = useMemo(() => {
    if (!tipoFalloFiltered) return tipoFalloFiltered;
    if (!nulidadOnly) return tipoFalloFiltered;
    return tipoFalloFiltered.filter((c) => c.flag_vuelve_despues_de_nulidad);
  }, [tipoFalloFiltered, nulidadOnly]);

  const visible = useMemo(() => (nulidadFiltered || []).slice((page - 1) * PAGE, page * PAGE), [nulidadFiltered, page]);
  const total = nulidadFiltered?.length || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  // feature/cases-grouping — group-by-tipo toggle, persisted per user.
  const [groupByTipo, setGroupByTipo] = useState(() => localStorage.getItem("eds.casesGroupByTipo") === "1");
  useEffect(() => { localStorage.setItem("eds.casesGroupByTipo", groupByTipo ? "1" : "0"); }, [groupByTipo]);
  // Insumos team priority order from Natali's transcript.
  const TIPO_ORDER = ["requerimiento","desacato","sancion","fallo","tutela","consulta"];
  const groupedRows = useMemo(() => {
    if (!groupByTipo) return null;
    const buckets = {};
    for (const c of visible) {
      const k = c.tipo || "otros";
      (buckets[k] = buckets[k] || []).push(c);
    }
    return TIPO_ORDER.filter(t => buckets[t]).map(t => ({ tipo: t, rows: buckets[t] }));
  }, [visible, groupByTipo]);

  // Optimistic patch helper for inline cell edits.
  async function patchCase(caseId, field, newValue, oldValue, label) {
    setData(arr => arr.map(c => c.id === caseId ? { ...c, [field]: newValue, ...(label || {}) } : c));
    let toastTimer = setTimeout(() => toast.push("Guardado"), 1000);
    try {
      await casesApi.update(caseId, { [field]: newValue });
      clearTimeout(toastTimer);
    } catch (e) {
      clearTimeout(toastTimer);
      setData(arr => arr.map(c => c.id === caseId ? { ...c, [field]: oldValue } : c));
      toast.push("No se pudo guardar — intente de nuevo", "error");
    }
  }

  // feature/depth-and-delete — soft-delete from context menu / row affordance.
  // Animate the row out before removing from local state, then refresh from
  // server so the cache is correct for filters.
  const [confirmDel, setConfirmDel] = useState(null); // { id, case_number }
  const [leavingIds, setLeavingIds] = useState([]);
  async function doSoftDelete(target) {
    setConfirmDel(null);
    if (!target) return;
    setLeavingIds(prev => [...prev, target.id]);
    try {
      await casesApi.softDelete(target.id);
      setTimeout(() => {
        setData(arr => arr ? arr.filter(c => c.id !== target.id) : arr);
        setLeavingIds(prev => prev.filter(x => x !== target.id));
        toast.push("Caso eliminado · Recuperable desde administración");
      }, 250);
    } catch (e) {
      setLeavingIds(prev => prev.filter(x => x !== target.id));
      toast.push(e?.response?.data?.error || "No se pudo eliminar", "error");
    }
  }
  function canDelete(c) {
    if (!user) return false;
    // feature/single-role-audit — multi-role aware.
    const roles = rolesOf(user);
    return ["admin","reparto","directora"].some(r => roles.includes(r))
        || c.created_by_id === user.id;
  }

  const { focusedRowId } = useHoverFocus();

  const tipoOptions = TIPOS.map(t => ({ value: t, label: TIPO_LABEL[t] }));
  const estadoOptions = ESTADOS.map(s => ({ value: s, label: ESTADO_LABEL[s] }));
  const abogadoSearch = async (qq) => {
    const ql = (qq || "").toLowerCase();
    // feature/single-role-audit — FALSE POSITIVE: list filter on PRIMARY
    // role; every active abogado has primary 'abogado' today.
    return allUsers
      .filter(u => u.role === "abogado" && (!qq || u.name.toLowerCase().includes(ql)))
      .map(u => ({ value: u.id, label: u.name }));
  };

  return (
    <div className="col" style={{ gap: 16 }}>
      <h1 style={{ margin: 0 }}>Casos</h1>

      <div className="card tight" style={{ padding: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 12 }}>
          <div>
            <label>Tipo de actuación</label>
            <PopoverMultiSelect
              values={tipos} options={tipoOptions} onChange={setTipos}
              placeholder="Todos" searchable={false}
            />
          </div>
          <div>
            <label>Estado de la actuación</label>
            <PopoverMultiSelect
              values={estados} options={estadoOptions} onChange={setEstados}
              placeholder="Todos" searchable={true}
            />
          </div>
          <div>
            <label>Abogado</label>
            <PopoverCombobox
              value={abogadoId}
              label={abogadoLabel}
              searchFn={abogadoSearch}
              placeholder="Todos"
              onChange={(id, opt) => { setAbogadoId(id); setAbogadoLabel(opt?.label || null); }}
            />
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <label>Buscar (radicado, accionante, número)</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" />
          </div>
        </div>
        <div style={{ display: "flex", marginTop: 12, alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
            <input type="checkbox" checked={groupByTipo} onChange={(e) => setGroupByTipo(e.target.checked)} />
            Agrupar por tipo de actuación
          </label>
          {/* feature/nulidad-bandera — sprint86. Toggle to isolate post-nulidad
              re-intakes. Only shown once at least one such case exists so the
              toolbar stays clean for everyone else. */}
          {nulidadCount > 0 && (
            <button type="button"
                    onClick={() => setNulidadOnly((v) => !v)}
                    className={nulidadOnly ? "" : "secondary"}
                    style={{ marginLeft: "auto", padding: "4px 10px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}
                    title="Mostrar solo tutelas que volvieron después de una nulidad">
              Vuelve después de nulidad
              <span style={{ background: nulidadOnly ? "rgba(255,255,255,0.25)" : "var(--color-warning-bg)", color: nulidadOnly ? "var(--on-accent)" : "var(--color-warning-ink)", fontSize: 10, fontWeight: 600, fontVariantNumeric: "tabular-nums", padding: "1px 6px", borderRadius: 10, minWidth: 16, textAlign: "center" }}>{nulidadCount}</span>
            </button>
          )}
          {/* feature/reparto-time-filters. */}
          <div style={{ display: "inline-flex", gap: 6, marginLeft: nulidadCount > 0 ? 0 : "auto" }}>
            <TimeFilterButton label="Todos" value="all" current={timeFilter} setter={setTimeFilter} />
            <TimeFilterButton label="Hoy" value="today" current={timeFilter} setter={setTimeFilter} count={todayCount} />
            <TimeFilterButton label="Esta semana" value="week" current={timeFilter} setter={setTimeFilter} count={weekCount} />
          </div>
        </div>
        {/* feature/estado-filter-pills. Counts come from
            the time-filtered slice so they stay in sync with the date
            pills above; pre-tipo_fallo so the user always sees the full
            active/closed split. */}
        <div style={{ marginTop: 12 }}>
          <EstadoFilterPills cases={filteredData || []}
                             value={estadoFilter}
                             onChange={setEstadoFilter} />
        </div>
        {/* feature/tipo-fallo-tabs. Visible only when Tipo de
            actuación is narrowed to fallo. Counts are computed from the
            estado-filtered slice so the active/closed pill state stays
            in sync. */}
        {showFalloTabs && (
          <div>
            <TipoFalloTabs cases={estadoFiltered || []}
                           value={tipoFalloTab}
                           onChange={setTipoFalloTab} />
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {data == null ? (
          /* feature/ui-polish-motion — skeleton shaped like the table
             (capped at 12 rows regardless of eventual list size). */
          <div style={{ padding: 16 }} aria-busy="true" aria-label="Cargando casos">
            <div className="skeleton" style={{ width: "100%", height: 32, marginBottom: 10 }} />
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="skeleton"
                   style={{ display: "block", width: "100%", height: 38, marginBottom: 6, opacity: 1 - i * 0.06 }} />
            ))}
          </div>
        ) : !total ? (
          <Empty title="Sin resultados" message={
            timeFilter === "today" ? "No hay casos creados hoy."
              : timeFilter === "week" ? "No hay casos creados esta semana."
              : "Ajuste los filtros para encontrar casos."
          } />
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Caso</th><th>Tipo de actuación</th><th>Accionante</th><th>Radicado</th>
                  <th>Juzgado</th><th>Abogado asignado</th><th>Estado de la actuación</th>
                  <th>Contestada</th>
                  <th>SLA</th>
                  <th>Última actividad</th>
                </tr>
              </thead>
              <tbody className={"eds-focus-list" + (focusedRowId ? " has-focused-row" : "")}>
                {groupByTipo && groupedRows
                  ? groupedRows.flatMap(g => [
                      <tr key={"group-" + g.tipo}>
                        <td colSpan={10} style={{ background: "var(--color-hover-bg)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-muted)", padding: "8px 12px" }}>
                          {g.tipo === "requerimiento" ? "Requerimientos"
                            : g.tipo === "desacato" ? "Desacatos"
                            : g.tipo === "sancion" ? "Sanciones"
                            : g.tipo === "fallo" ? "Fallos"
                            : g.tipo === "tutela" ? "Tutelas"
                            : g.tipo === "consulta" ? "Consultas"
                            : g.tipo} ({g.rows.length})
                        </td>
                      </tr>,
                      ...g.rows.map(c => (
                        <CaseRow
                          key={c.id} c={c}
                          canEdit={canEdit} canEditAbogado={canEditAbogado}
                          canDelete={canDelete(c)}
                          leaving={leavingIds.includes(c.id)}
                          onOpen={() => setOpenId(c.id)}
                          onPatch={patchCase}
                          onAskDelete={() => setConfirmDel({ id: c.id, case_number: c.case_number })}
                          abogados={allUsers.filter(u => u.role === "abogado")}
                        />
                      )),
                    ])
                  : visible.map(c => (
                    <CaseRow
                      key={c.id} c={c}
                      canEdit={canEdit} canEditAbogado={canEditAbogado}
                      canDelete={canDelete(c)}
                      leaving={leavingIds.includes(c.id)}
                      onOpen={() => setOpenId(c.id)}
                      onPatch={patchCase}
                      onAskDelete={() => setConfirmDel({ id: c.id, case_number: c.case_number })}
                      abogados={allUsers.filter(u => u.role === "abogado")}
                    />
                  ))}
              </tbody>
            </table>
            <div style={{ display: "flex", alignItems: "center", padding: "10px 14px", borderTop: "1px solid var(--border-hairline)" }}>
              <span className="muted" style={{ fontSize: 12 }}>{total} casos · página {page}/{pages}</span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button className="secondary sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</button>
                <button className="secondary sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Siguiente</button>
              </div>
            </div>
          </>
        )}
      </div>

      <CaseDetail
        caseId={openId} open={!!openId}
        defaultTab={defaultTab}
        onClose={closePanel}
        onChanged={load}
      />

      <Confirm
        open={!!confirmDel}
        title="¿Eliminar este caso?"
        message="El caso será movido a la papelera. Podrá recuperarlo desde la sección de administración. ¿Continuar?"
        onClose={() => setConfirmDel(null)}
        onConfirm={() => doSoftDelete(confirmDel)}
        confirmText="Eliminar"
        danger
      />
    </div>
  );
}

// =====================================================================
// CaseRow — table row with inline cell editing.
// =====================================================================
function CaseRow({ c, canEdit, canEditAbogado, canDelete, leaving, onOpen, onPatch, onAskDelete, abogados }) {
  const [editingCell, setEditingCell] = useState(null); // 'radicado' | 'juzgado' | 'accionante' | 'abogado'
  const [tooltip, setTooltip] = useState(null); // { msg, x, y }
  // feature/apple-treatment — hover-intent + shared focus state. The row's own focus turns on
  // after 300ms of hover; the surrounding context is told so siblings recede.
  const { setFocusedRowId } = useHoverFocus();
  const intent = useHoverIntent(300, 100);
  useEffect(() => {
    if (intent.isFocused) setFocusedRowId(c.id);
    else setFocusedRowId(prev => (prev === c.id ? null : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent.isFocused, c.id]);

  // feature/depth-and-delete — right-click affordance. Hidden items collapse; if no items
  // would render, the menu just won't open.
  const { onContextMenu, menu } = useContextMenu([
    { label: "Abrir caso",    icon: "↗",  onClick: onOpen },
    { label: "Copiar enlace", icon: "🔗", onClick: () => {
      const url = `${window.location.origin}/cases?open=${c.id}`;
      navigator.clipboard?.writeText(url).catch(() => {});
    }},
    ...(canDelete ? ["divider", { label: "Eliminar caso", icon: "🗑", onClick: onAskDelete, destructive: true }] : []),
  ]);

  function clickRow(e) {
    if (editingCell) return;
    if (e.target.closest(".no-row-click")) return;
    onOpen();
  }
  function showTooltip(e, msg) {
    setTooltip({ msg, x: e.clientX, y: e.clientY + 16 });
    setTimeout(() => setTooltip(null), 2000);
  }

  return (
    <>
    <tr
      onClick={clickRow}
      onContextMenu={onContextMenu}
      onMouseEnter={intent.onMouseEnter}
      onMouseLeave={intent.onMouseLeave}
      style={{ cursor: "pointer" }}
      className={"eds-focus-row "
                 + (intent.isFocused ? "is-focused " : "")
                 + (editingCell ? "editing-row " : "")
                 + (leaving ? "row-leaving" : "")}
    >
      <td>
        {c.urgente && <span title="Actuación urgente" style={{ color: "var(--color-danger)", marginRight: 4 }}>🚩</span>}
        <strong>{c.case_number}</strong>
        {c.late_entry && (
          <span className={"badge " + (c.late_entry_approved ? "muted" : "warning")} style={{ marginLeft: 6 }}>
            {c.late_entry_approved ? "Extemporáneo ✓" : "Extemporáneo"}
          </span>
        )}
      </td>
      <td>
        <TipoPill tipo={c.tipo} />
        {c.tipo === "fallo" && (
          <div style={{ marginTop: 4 }}>
            <TipoFalloPill value={c.tipo_fallo} />
          </div>
        )}
        {(c.sub_tipo || c.no_aplica) && (
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
            {formatTipo(c.tipo, c.sub_tipo, c.no_aplica)}
          </div>
        )}
      </td>

      <EditableCell
        value={c.accionante_nombre}
        canEdit={canEdit}
        editing={editingCell === "accionante"}
        onStartEdit={() => setEditingCell("accionante")}
        onTooltip={(e) => showTooltip(e, "No tiene permiso para editar este campo")}
        editor={
          <PersonaCombobox
            value={c.persona_id}
            label={c.accionante_nombre}
            placeholder="Buscar accionante…"
            onChange={(personaId, opt) => {
              if (!personaId) return;
              setEditingCell(null);
              onPatch(c.id, "persona_id", personaId, c.persona_id, { accionante_nombre: opt?.label });
            }}
          />
        }
      />

      <EditableCell
        value={c.radicado}
        canEdit={canEdit}
        editing={editingCell === "radicado"}
        onStartEdit={() => setEditingCell("radicado")}
        onTooltip={(e) => showTooltip(e, "No tiene permiso para editar este campo")}
        editor={
          <CellInput
            initial={c.radicado}
            onCommit={(v) => { setEditingCell(null); if (v !== c.radicado) onPatch(c.id, "radicado", v, c.radicado); }}
            onCancel={() => setEditingCell(null)}
          />
        }
      />

      <EditableCell
        value={c.juzgado || "—"}
        canEdit={canEdit}
        editing={editingCell === "juzgado"}
        onStartEdit={() => setEditingCell("juzgado")}
        onTooltip={(e) => showTooltip(e, "No tiene permiso para editar este campo")}
        editor={
          <CellInput
            initial={c.juzgado || ""}
            onCommit={(v) => { setEditingCell(null); if (v !== (c.juzgado || "")) onPatch(c.id, "juzgado", v || null, c.juzgado); }}
            onCancel={() => setEditingCell(null)}
          />
        }
      />

      <EditableCell
        value={c.abogado_nombre || <span className="muted">—</span>}
        canEdit={canEditAbogado}
        editing={editingCell === "abogado"}
        onStartEdit={() => setEditingCell("abogado")}
        onTooltip={(e) => showTooltip(e, "No tiene permiso para editar este campo")}
        editor={
          <PopoverCombobox
            value={c.abogado_asignado_id}
            label={c.abogado_nombre}
            placeholder="Asignar abogado…"
            options={abogados.map(u => ({ value: u.id, label: u.name }))}
            onChange={(id, opt) => {
              setEditingCell(null);
              if (id !== c.abogado_asignado_id) onPatch(c.id, "abogado_asignado_id", id, c.abogado_asignado_id, { abogado_nombre: opt?.label });
            }}
          />
        }
      />

      <td><EstadoPill c={c} /></td>

      {/* feature/contestada-tag. */}
      <td><ContestadaTag contestadaAt={c.contestada_at} estado={c.estado} dateFormat="short" /></td>

      <td onClick={(e) => { e.stopPropagation(); showTooltip(e, "Calculado automáticamente"); }}>
        <SlaPill c={c} />
      </td>
      <td className="muted" style={{ fontSize: 12 }}>
        {c.updated_at ? new Date(c.updated_at).toLocaleString("es-CO") : "—"}
      </td>

      {tooltip && (
        <td style={{ padding: 0, border: "none" }}>
          <div className="cell-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>{tooltip.msg}</div>
        </td>
      )}
    </tr>
    {menu}
    </>
  );
}

// Generic "click to edit" cell. Renders the value in idle/hover; swaps to
// the provided editor while editing. The editor is responsible for calling
// onCommit/onCancel which set editing=false in the parent.
function EditableCell({ value, canEdit, editing, onStartEdit, onTooltip, editor }) {
  if (editing) {
    return <td className="editable editing no-row-click" onClick={(e) => e.stopPropagation()}>{editor}</td>;
  }
  return (
    <td
      className="editable no-row-click"
      data-can-edit={canEdit ? "true" : "false"}
      onClick={(e) => {
        e.stopPropagation();
        if (canEdit) onStartEdit();
        else onTooltip(e);
      }}
    >
      {value}
      {canEdit && <span className="cell-pencil" aria-hidden="true">✎</span>}
    </td>
  );
}

function CellInput({ initial, onCommit, onCancel }) {
  const [v, setV] = useState(initial || "");
  const [cancelled, setCancelled] = useState(false);
  return (
    <input
      autoFocus
      className="cell-input"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onFocus={(e) => e.target.select()}
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit(v);
        else if (e.key === "Escape") { setCancelled(true); onCancel(); }
      }}
      onBlur={() => { if (!cancelled) onCommit(v); }}
    />
  );
}
